import { useEffect } from "react";

import { FormProvider, UseFormReturn } from "react-hook-form";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";

import FormPhoneField from "@covertree/ui/forms/FormPhoneField";
import { Button, Form, FormEmailAutocorrect } from "@covertree/ui";
import { AGENT_HELP_ADDRESS } from "@consts/index";
import { EditContactForm } from "./form";

interface UpdateContactInfoForm {
  loading: boolean;
  isNoEmail: boolean;
  form: UseFormReturn<EditContactForm>;
  onClose: () => void;
  onSubmit: (formValues: EditContactForm) => Promise<void>;
}

export function UpdateContactInfoForm({
  onClose,
  loading,
  isNoEmail,
  form,
  onSubmit,
}: UpdateContactInfoForm) {
  const { control, handleSubmit: submit, setValue } = form;

  useEffect(() => {
    if (isNoEmail) setValue("emailAddress", "");
  }, [isNoEmail, setValue]);

  return (
    <Form onSubmit={submit(onSubmit)}>
      <FormProvider {...form}>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={6} mt={-2}>
            <FormPhoneField
              id="phone-number-input"
              dataTestId="phone-number-input"
              name="primaryContactNumber"
              label="Phone number"
              control={control}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={6} mt={-2}>
            <FormPhoneField
              id="second-phone-number-input"
              dataTestId="second-phone-number-input"
              name="secondaryContactNumber"
              label="Secondary phone number"
              control={control}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} mt={-2} mb={2}>
            <FormEmailAutocorrect
              id="email-input"
              dataTestId="email-input"
              name="emailAddress"
              label="Email"
              control={control}
              setValue={setValue}
              isEnabled
              disabled={isNoEmail || loading}
            />
          </Grid>

          {isNoEmail ? (
            <Grid item xs={12}>
              <Typography variant="subtitle1" mb={-2}>
                This policy was issued without an email address. In order to add
                one, an endorsement must be issued. Please contact us at{" "}
                <Link
                  variant="subtitle1"
                  color="secondary"
                  href={`mailto:${AGENT_HELP_ADDRESS}`}
                  target="_blank"
                  rel="noreferrer">
                  {AGENT_HELP_ADDRESS}
                </Link>{" "}
                for further assistance.
              </Typography>
            </Grid>
          ) : (
            <Grid xs={12} ml={2}>
              <Typography variant="subtitle1" color="textSecondary">
                If this policyholder has any policies in status &#34;Pending
                E-Sign&#34;, then a new e-sign reminder email will be sent to
                the new email address provided, for each applicable policy.
              </Typography>
            </Grid>
          )}
        </Grid>
      </FormProvider>

      <Box mt={2}>
        <DialogActions>
          <Button
            type="submit"
            data-testid="submit-edit-contact-form"
            loading={loading}
            disabled={loading}>
            Submit
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            data-testid="cancel-edit-contact-form"
            disabled={loading}>
            Cancel
          </Button>
        </DialogActions>
      </Box>
    </Form>
  );
}
